import type { Atlas, MeshData } from "./wasm";

// WebGL2 renderer for one structure. The vertex layout and the shaders are the
// same ones the desktop viewer uses (src/schemstudio/schem_view.cpp), and the
// mesh comes from the same C++ builder -- so a build looks identical in the app
// and in the browser, which is the whole reason the core is shared.

const VERT = `#version 300 es
layout(location=0) in vec3 a_pos;
layout(location=1) in vec2 a_uv;
layout(location=2) in vec3 a_col;
layout(location=3) in float a_tex;
uniform mat4 u_mvp;
uniform vec2 u_atlas_size;
out vec2 v_uv;
out vec3 v_col;
out float v_tex;
void main() {
  v_uv = a_uv / max(u_atlas_size, vec2(1.0));
  v_col = a_col;
  v_tex = a_tex;
  gl_Position = u_mvp * vec4(a_pos, 1.0);
}`;

const FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
in vec3 v_col;
in float v_tex;
uniform sampler2D u_tex;
uniform float u_alpha;
out vec4 frag;
void main() {
  vec4 c = vec4(v_col, 1.0);
  if (v_tex > 0.5) {
    vec4 t = texture(u_tex, v_uv);
    if (t.a < 0.1) discard;
    c = vec4(t.rgb * v_col, t.a);
  }
  frag = vec4(c.rgb, c.a * u_alpha);
}`;

const STRIDE = 9 * 4; // pos3 + uv2 + col3 + tex1, floats

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const sh = gl.createShader(type)!;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS))
    throw new Error("shader: " + gl.getShaderInfoLog(sh));
  return sh;
}

class Batch {
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  count = 0;
  constructor(private gl: WebGL2RenderingContext) {
    this.vao = gl.createVertexArray()!;
    this.vbo = gl.createBuffer()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, STRIDE, 0);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, STRIDE, 12);
    gl.vertexAttribPointer(2, 3, gl.FLOAT, false, STRIDE, 20);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, STRIDE, 32);
    for (let i = 0; i < 4; ++i) gl.enableVertexAttribArray(i);
    gl.bindVertexArray(null);
  }
  upload(data: Float32Array) {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
    this.count = data.length / 9;
  }
  draw(mode: number) {
    if (!this.count) return;
    const gl = this.gl;
    gl.bindVertexArray(this.vao);
    gl.drawArrays(mode, 0, this.count);
  }
  dispose() {
    this.gl.deleteBuffer(this.vbo);
    this.gl.deleteVertexArray(this.vao);
  }
}

// Column-major 4x4, the order GL wants.
function perspective(fovyDeg: number, aspect: number, near: number, far: number) {
  const f = 1 / Math.tan((fovyDeg * Math.PI) / 360);
  const nf = 1 / (near - far);
  return new Float32Array([
    f / aspect, 0, 0, 0,
    0, f, 0, 0,
    0, 0, (far + near) * nf, -1,
    0, 0, 2 * far * near * nf, 0,
  ]);
}

function lookAt(eye: number[], target: number[], up: number[]) {
  const z = norm(sub(eye, target));
  const x = norm(cross(up, z));
  const y = cross(z, x);
  return new Float32Array([
    x[0], y[0], z[0], 0,
    x[1], y[1], z[1], 0,
    x[2], y[2], z[2], 0,
    -dot(x, eye), -dot(y, eye), -dot(z, eye), 1,
  ]);
}

const sub = (a: number[], b: number[]) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: number[], b: number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: number[], b: number[]) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
function norm(v: number[]) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

function multiply(a: Float32Array, b: Float32Array) {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; ++c)
    for (let r = 0; r < 4; ++r) {
      let s = 0;
      for (let k = 0; k < 4; ++k) s += a[k * 4 + r] * b[c * 4 + k];
      o[c * 4 + r] = s;
    }
  return o;
}

export class StructureRenderer {
  private gl: WebGL2RenderingContext;
  private prog: WebGLProgram;
  private solid: Batch;
  private tris: Batch;
  private lines: Batch;
  private ghost: Batch;
  private tex: WebGLTexture | null = null;
  private atlasSize: [number, number] = [1, 1];

  // Orbit camera, same defaults and feel as the desktop viewer.
  private yaw = 35;
  private pitch = 28;
  private dist = 40;
  private target: [number, number, number] = [0, 0, 0];
  private dragging: number | null = null;
  private last: [number, number] = [0, 0];

  constructor(private canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl2", { antialias: true, alpha: false });
    if (!gl) throw new Error("This browser has no WebGL2.");
    this.gl = gl;
    this.prog = gl.createProgram()!;
    gl.attachShader(this.prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(this.prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(this.prog);
    if (!gl.getProgramParameter(this.prog, gl.LINK_STATUS))
      throw new Error("link: " + gl.getProgramInfoLog(this.prog));
    this.solid = new Batch(gl);
    this.tris = new Batch(gl);
    this.lines = new Batch(gl);
    this.ghost = new Batch(gl);
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    this.bindInput();
  }

  private bindInput() {
    const c = this.canvas;
    c.addEventListener("pointerdown", (e) => {
      this.dragging = e.button;
      this.last = [e.clientX, e.clientY];
      c.setPointerCapture(e.pointerId);
    });
    c.addEventListener("pointerup", (e) => {
      this.dragging = null;
      c.releasePointerCapture(e.pointerId);
    });
    c.addEventListener("pointermove", (e) => {
      if (this.dragging === null) return;
      const dx = e.clientX - this.last[0];
      const dy = e.clientY - this.last[1];
      this.last = [e.clientX, e.clientY];
      if (this.dragging === 0) {
        this.yaw -= dx * 0.4;
        this.pitch = Math.max(-89, Math.min(89, this.pitch + dy * 0.4));
      } else {
        const ry = (this.yaw * Math.PI) / 180;
        const k = this.dist * 0.0016;
        this.target[0] -= Math.cos(ry) * dx * k;
        this.target[2] += Math.sin(ry) * dx * k;
        this.target[1] += dy * k;
      }
      this.draw();
    });
    c.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        this.dist = Math.max(2, Math.min(3000, this.dist * Math.pow(0.9, -e.deltaY / 120)));
        this.draw();
      },
      { passive: false },
    );
    c.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  frame(sx: number, sy: number, sz: number) {
    this.target = [sx / 2, sy * 0.4, sz / 2];
    this.dist = Math.max(8, Math.max(sx, sy, sz) * 1.9);
    this.draw();
  }

  setAtlas(atlas: Atlas | null) {
    const gl = this.gl;
    if (this.tex) gl.deleteTexture(this.tex);
    this.tex = null;
    if (!atlas) return;
    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, atlas.width, atlas.height, 0, gl.RGBA,
      gl.UNSIGNED_BYTE, atlas.pixels,
    );
    gl.generateMipmap(gl.TEXTURE_2D);
    // Nearest magnification keeps the pixel-art look; mipmapped minification
    // stops the atlas from sparkling when zoomed out.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.atlasSize = [atlas.width, atlas.height];
  }

  setMesh(mesh: MeshData) {
    this.solid.upload(mesh.solid);
    this.tris.upload(mesh.overlayTris);
    this.lines.upload(mesh.overlayLines);
    this.ghost.upload(mesh.ghost);
    this.draw();
  }

  clear() {
    const empty = new Float32Array(0);
    this.solid.upload(empty);
    this.tris.upload(empty);
    this.lines.upload(empty);
    this.ghost.upload(empty);
    this.draw();
  }

  draw() {
    const gl = this.gl;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.floor(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    gl.clearColor(0.13, 0.14, 0.16, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    const rp = (this.pitch * Math.PI) / 180;
    const ry = (this.yaw * Math.PI) / 180;
    const eye = [
      this.target[0] + Math.cos(rp) * Math.sin(ry) * this.dist,
      this.target[1] + Math.sin(rp) * this.dist,
      this.target[2] + Math.cos(rp) * Math.cos(ry) * this.dist,
    ];
    const mvp = multiply(
      perspective(45, w / h, 0.1, 4000),
      lookAt(eye, this.target as number[], [0, 1, 0]),
    );

    gl.useProgram(this.prog);
    gl.uniformMatrix4fv(gl.getUniformLocation(this.prog, "u_mvp"), false, mvp);
    gl.uniform2f(
      gl.getUniformLocation(this.prog, "u_atlas_size"),
      this.atlasSize[0], this.atlasSize[1],
    );
    gl.uniform1i(gl.getUniformLocation(this.prog, "u_tex"), 0);
    gl.uniform1f(gl.getUniformLocation(this.prog, "u_alpha"), 1);
    gl.activeTexture(gl.TEXTURE0);
    if (this.tex) gl.bindTexture(gl.TEXTURE_2D, this.tex);

    this.tris.draw(gl.TRIANGLES);
    this.solid.draw(gl.TRIANGLES);
    this.lines.draw(gl.LINES);
    // Carve / foundation volumes last: translucent and without depth writes,
    // so overlapping faces blend instead of hiding each other.
    if (this.ghost.count) {
      gl.uniform1f(gl.getUniformLocation(this.prog, "u_alpha"), 0.42);
      gl.depthMask(false);
      this.ghost.draw(gl.TRIANGLES);
      gl.depthMask(true);
    }
    gl.bindVertexArray(null);
  }

  dispose() {
    this.solid.dispose();
    this.tris.dispose();
    this.lines.dispose();
    this.ghost.dispose();
    if (this.tex) this.gl.deleteTexture(this.tex);
    this.gl.deleteProgram(this.prog);
  }
}
